import React, { useMemo, useState } from "react";
import {
  FaAt,
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaMailBulk,
  FaPhoneAlt,
  FaRegCommentDots,
  FaRocket,
  FaShieldAlt,
  FaUser,
} from "react-icons/fa";

import { saveMessageContactForm } from "../../services/contactformServices";

const MAX_MESSAGE = 1000;

const isEmail = (value = "") =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value.trim());

const CONTACT_PHONE = "06 80 72 77 07";
const CONTACT_PHONE_HREF = "tel:+33680727707";

const Field = ({
  id,
  name,
  type = "text",
  label,
  value,
  onChange,
  onBlur,
  error,
  icon: Icon,
  textarea = false,
  rows = 6,
  maxLength,
  inputMode,
}) => {
  const [focused, setFocused] = useState(false);
  const describedBy = error ? `${id}-error` : undefined;

  const hasValue = String(value || "").trim().length > 0;

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = (event) => {
    setFocused(false);
    onBlur?.(event);
  };

  const fieldClass = `
    block w-full rounded-2xl border bg-white/90 text-sm text-slate-950
    shadow-sm outline-none transition placeholder:text-transparent
    focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10
    dark:bg-slate-950/60 dark:text-white
    dark:focus:border-orange-300 dark:focus:ring-orange-300/10
    ${
      error
        ? "border-red-300 dark:border-red-500/60"
        : "border-slate-200 dark:border-white/10"
    }
  `;

  const labelClass = `
    pointer-events-none absolute z-10 text-xs font-bold transition
    ${
      focused || hasValue
        ? "top-2 text-orange-600 dark:text-orange-300"
        : "top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
    }
    ${Icon ? "left-11" : "left-4"}
  `;

  return (
    <div>
      <div className="relative">
        {Icon && (
          <span
            className={`pointer-events-none absolute left-4 z-10 text-slate-400 dark:text-slate-500 ${
              textarea ? "top-5" : "top-1/2 -translate-y-1/2"
            }`}
          >
            <Icon className="h-4 w-4" />
          </span>
        )}

        <label htmlFor={id} className={labelClass}>
          {label}
        </label>

        {textarea ? (
          <textarea
            id={id}
            name={name}
            rows={rows}
            maxLength={maxLength}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder=" "
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            className={`${fieldClass} min-h-[170px] resize-y pb-4 pt-9 ${
              Icon ? "pl-11 pr-4" : "px-4"
            }`}
          />
        ) : (
          <input
            id={id}
            name={name}
            type={type}
            inputMode={inputMode}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder=" "
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            className={`${fieldClass} h-16 pb-2 pt-7 ${
              Icon ? "pl-11 pr-4" : "px-4"
            }`}
          />
        )}
      </div>

      <div className="mt-1 min-h-5">
        {error ? (
          <p
            id={describedBy}
            className="text-xs font-medium text-red-600 dark:text-red-400"
          >
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, text }) => {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/10 p-4 backdrop-blur">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-orange-200">
          <Icon className="h-4 w-4" />
        </div>

        <div>
          <h3 className="font-bold text-white">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-slate-300">{text}</p>
        </div>
      </div>
    </div>
  );
};

const FormContact = () => {
  const [notificationSent, setNotificationSent] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [touched, setTouched] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const errors = useMemo(() => {
    const nextErrors = {};

    if (touched.name && !formData.name.trim()) {
      nextErrors.name = "Le nom est requis.";
    }

    if (touched.email && !isEmail(formData.email)) {
      nextErrors.email = "Veuillez renseigner une adresse email valide.";
    }

    if (touched.message && !formData.message.trim()) {
      nextErrors.message = "Le message est requis.";
    }

    if (touched.message && formData.message.length > MAX_MESSAGE) {
      nextErrors.message = `Message trop long (${formData.message.length}/${MAX_MESSAGE}).`;
    }

    return nextErrors;
  }, [formData, touched]);

  const isValid =
    formData.name.trim() &&
    isEmail(formData.email) &&
    formData.message.trim() &&
    formData.message.length <= MAX_MESSAGE;

  const handleChange = (event) => {
    const { name, value } = event.target;

    setNotificationSent(false);
    setSubmitError("");

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleBlur = (event) => {
    const { name } = event.target;

    setTouched((current) => ({
      ...current,
      [name]: true,
    }));
  };

  const fillDemoMessage = () => {
    setNotificationSent(false);
    setSubmitError("");

    const phoneLine = formData.phone?.trim()
      ? `Téléphone : ${formData.phone.trim()}\n`
      : "";

    setFormData((current) => ({
      ...current,
      message:
        `Bonjour,\n\n` +
        `Je souhaite obtenir une démonstration de MonAppliTournoi pour organiser une compétition.\n\n` +
        `${phoneLine}` +
        `Pouvez-vous me recontacter afin d’échanger sur la mise en place et les fonctionnalités ?\n\n` +
        `Merci.`,
    }));

    setTouched((current) => ({
      ...current,
      message: true,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setTouched({
      name: true,
      email: true,
      message: true,
    });

    setSubmitError("");
    setNotificationSent(false);

    if (!isValid) return;
    if (honeypot) return;

    try {
      setLoading(true);

      await saveMessageContactForm({
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      });

      setNotificationSent(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      setTouched({});
    } catch (error) {
      console.error("Erreur lors de l'envoi du message :", error);
      setSubmitError(
        "Une erreur est survenue lors de l’envoi du message. Vous pouvez aussi me contacter directement par téléphone.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-blue-300/20 blur-3xl dark:bg-blue-500/10" />

      <div className="relative mx-auto grid max-w-[1180px] overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-950/10 dark:border-white/10 dark:bg-slate-900 lg:grid-cols-[0.9fr_1.1fr]">
        <aside className="relative overflow-hidden bg-slate-950 p-6 text-white sm:p-8 lg:p-10">
          <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-orange-500/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-20 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-200">
              <FaRocket className="h-3.5 w-3.5" />
              Démonstration rapide
            </div>

            <h3 className="mt-5 text-3xl font-bold tracking-tight sm:text-4xl">
              Parlons de votre prochaine compétition.
            </h3>

            <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
              Une question, une démo ou un besoin spécifique pour votre tournoi
              ? Envoyez un message et je vous réponds rapidement pour vous
              guider sur la meilleure formule et la mise en place.
            </p>

            <div className="mt-7 grid gap-3">
              <InfoCard
                icon={FaClock}
                title="Démo en visio"
                text="Une présentation courte permet de comprendre le déroulé complet : import, pesée, poules, tableaux, tapis et résultats."
              />

              <InfoCard
                icon={FaShieldAlt}
                title="Mise en place accompagnée"
                text="Je peux vous aider à préparer votre première compétition et vérifier les bons réglages."
              />

              <InfoCard
                icon={FaPhoneAlt}
                title="Contact direct"
                text={`Téléphone : ${CONTACT_PHONE}`}
              />
            </div>

            <div className="mt-7 rounded-3xl border border-orange-300/20 bg-orange-400/10 p-4">
              <p className="text-sm font-bold text-orange-100">
                Besoin d’une réponse rapide ?
              </p>

              <a
                href={CONTACT_PHONE_HREF}
                className="mt-2 inline-flex items-center gap-2 text-lg font-bold text-white transition hover:text-orange-200"
              >
                <FaPhoneAlt className="h-4 w-4" />
                {CONTACT_PHONE}
              </a>
            </div>
          </div>
        </aside>

        <div className="p-4 sm:p-6 lg:p-8">
          {notificationSent && (
            <div className="mb-5 flex items-start gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-300">
              <FaCheckCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-bold">Message envoyé avec succès.</p>
                <p className="mt-1 text-sm">
                  Merci, je vous répondrai dès que possible.
                </p>
              </div>
            </div>
          )}

          {submitError && (
            <div className="mb-5 rounded-3xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700 dark:border-red-400/20 dark:bg-red-500/10 dark:text-red-300">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <input
              type="text"
              name="company"
              tabIndex="-1"
              autoComplete="off"
              value={honeypot}
              onChange={(event) => setHoneypot(event.target.value)}
              className="hidden"
            />

            <div className="mb-5 flex flex-col gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950/40 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Gagnez du temps
                </p>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Vous pouvez préremplir un message de demande de démonstration.
                </p>
              </div>

              <button
                type="button"
                onClick={fillDemoMessage}
                className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-orange-200 bg-white px-4 py-2 text-sm font-bold text-orange-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-orange-50 dark:border-orange-400/20 dark:bg-white/[0.04] dark:text-orange-300 dark:hover:bg-orange-500/10"
              >
                Préremplir
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id="name"
                name="name"
                label="Nom Prénom"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.name}
                icon={FaUser}
              />

              <Field
                id="email"
                name="email"
                type="email"
                label="Votre email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.email}
                icon={FaAt}
              />
            </div>

            <div className="mt-2">
              <Field
                id="phone"
                name="phone"
                type="tel"
                label="Téléphone optionnel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                error={null}
                icon={FaPhoneAlt}
              />
            </div>

            <div className="mt-2">
              <Field
                id="message"
                name="message"
                label="Votre message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.message}
                icon={FaRegCommentDots}
                textarea
                rows={7}
                maxLength={MAX_MESSAGE}
              />

              <div className="mt-[-10px] mb-4 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
                <span>
                  Décrivez votre tournoi, votre besoin ou vos questions.
                </span>

                <span
                  className={
                    formData.message.length > MAX_MESSAGE * 0.9
                      ? "font-bold text-orange-600 dark:text-orange-300"
                      : ""
                  }
                >
                  {formData.message.length}/{MAX_MESSAGE}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Vos informations servent uniquement à répondre à votre demande.
              </p>

              <button
                type="submit"
                disabled={!isValid || loading}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-orange-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaMailBulk className="h-4 w-4" />
                {loading ? "Envoi en cours..." : "Envoyer le message"}
              </button>
            </div>
          </form>

          <div className="mt-6 flex items-center gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950/40">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
              <FaEnvelope className="h-4 w-4" />
            </div>

            <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              Vous pouvez aussi envoyer une demande pour un devis, une aide à la
              première configuration ou une question sur les formules.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormContact;
